import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import moment from 'moment';

import {
  DATE_FORMAT_DOT_MDY,
  DATE_FORMAT_SLASH_MDY,
  DATE_FORMAT_YMD
} from '../../constants/dateFormatsConst';
import { NODATA } from '../../constants/messageConst';
import { getWeekDays, getWeekRange, showMsg, displayDealershipName } from '../../services/library';
import DashboardService from '../../services/dashboardService';
import UserService from '../../services/userService';
import { STORAGE_DATAFILTER } from '../../constants/storageConst';
import DealerService from '../../services/dealerService';
import UserSettingsService from '../../services/usersettingsService';
import { arraysAreEqual } from '../../generalUtils/commonFn';
import InventoryService from '../../services/inventoryService';
import StatsService from '../../services/statsService';

const REQ_DASHBOARD = 'dashboard/';

export const retriveDataFilterStorage = (dealerId = null) => {
  const locallyStored =
    localStorage.getItem(STORAGE_DATAFILTER) || sessionStorage.getItem(STORAGE_DATAFILTER);

  if (!locallyStored || ['undefined', 'null', ''].includes(locallyStored)) {
    if (dealerId && typeof dealerId === 'number') {
      return [dealerId];
    }
    return dealerId;
  }
  const storedFilter = locallyStored?.split(',')?.map(Number);
  if (storedFilter && storedFilter?.length === 1 && dealerId && storedFilter?.[0] !== dealerId) {
    return [dealerId];
  }
  return storedFilter;
};

export const getFilteredDealersToggleLabel = ({ isAllDlr, allDealersId, dataFilter = [], dealer = {} }) => {
  const dataFilterLength = dataFilter?.length ?? 0;
  const hasFilter = dataFilterLength && (isAllDlr || dataFilter[0] !== dealer?.value || dataFilterLength > 1);
  const filteredCount = isAllDlr ? allDealersId?.length : dataFilterLength;

  if (!hasFilter) {
    return displayDealershipName(dealer?.label);
  }

  const count = Number(filteredCount).toLocaleString('US');

  if (isAllDlr) {
    return `${count} Active Dealerships`;
  }

  return `Total of ${count} filtered dealerships`;
};

export const fetchDashboardCountPAReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchDashboardCountPAReq`,
  async ({ dealers, summaryStartDate, summaryEndDate, isAll }, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getDashboardCount({
        dealers,
        isAll,
        fromdate: moment(summaryStartDate, DATE_FORMAT_SLASH_MDY).format(DATE_FORMAT_YMD),
        todate: moment(summaryEndDate, DATE_FORMAT_SLASH_MDY).format(DATE_FORMAT_YMD)
      });
      if (response?.status) {
        return response.data[0];
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchMonthCountReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchMonthCountReq`,
  async ({ dealers, monthStartDate, isAll }, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getMonthCount({
        dealers,
        month_date: moment(monthStartDate, DATE_FORMAT_SLASH_MDY).format(DATE_FORMAT_YMD),
        isAll
      });
      if (response?.status) {
        return response.data[0];
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchPublishedChartReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchPublishedChartReq`,
  async ({ dealers, selectedYear, isAll }, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getPublishedChart({
        dealers,
        year: selectedYear,
        isAll
      });
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchPublishedChartAveragesReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchPublishedChartAveragesReq`,
  async ({ dealers, isAll }, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getPublishedChartAverages({
        dealers,
        isAll
      });
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchAvgTtmChartReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchAvgTtmChartReq`,
  async (data, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getAvgTtmStats(data);
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchTurnTrendChartData = createAsyncThunk(
  `${REQ_DASHBOARD}fetchTurnTrendChartData`,
  async (data, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getTurnTrendChartData(data);
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchFlagChartReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchFlagChartReq`,
  async ({ dealers, flagYear, isAll }, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getFlagChart({
        dealers,
        year: flagYear,
        isAll
      });
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const downloadFlagDataReq = createAsyncThunk(
  `${REQ_DASHBOARD}downloadFlagDataReq`,
  async ({ startDate, endDate, dealers, isAll }) => {
    try {
      const response = await DashboardService.downloadFlagData({
        fromdate: moment(startDate).format(DATE_FORMAT_YMD),
        todate: moment(endDate).format(DATE_FORMAT_YMD),
        isAll,
        dealers
      });
      if (response.type === 'application/json') {
        let reader = new FileReader();
        reader.onload = function () {
          showMsg(JSON.parse(reader.result).errors[0], 'error');
        };
        reader.readAsText(response);
      } else {
        const fileName = `Flags(${moment(startDate).format(DATE_FORMAT_DOT_MDY)} - ${moment(endDate).format(
          DATE_FORMAT_DOT_MDY
        )}).xlsx`;
        const url = window.URL.createObjectURL(
          new Blob([response], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
      }
    } catch (error) {
      showMsg(error, 'error');
    }
  }
);

export const fetchPaProfileReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchPaProfileReq`,
  async ({ dealer_id, sdate, edate, selectedPhotographer }, { rejectWithValue }) => {
    try {
      const response = await DealerService.getPaProfile({
        dealer_id,
        sdate: moment(sdate).format(DATE_FORMAT_YMD),
        edate: moment(edate).format(DATE_FORMAT_YMD),
        selectedPhotographer
      });
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchPresavedSelectionsReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchPresavedSelectionsReq`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserSettingsService.getPresavedUserSelections();
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchDealerList4SelectReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchDealerList4SelectReq`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserService.getDealerList4Select();
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchDmsImportExportReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchDmsImportExportReq`,
  async (dealer_id, { rejectWithValue }) => {
    try {
      const response = await DealerService.getDealerDmsImportExport({ dealer_id });
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchVehicleCutoutsReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchVehicleCutoutsReq`,
  async ({ dealers, startDate, endDate, isAll }, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getCutouts({
        dealers,
        fromdate: moment(startDate, DATE_FORMAT_SLASH_MDY).format(DATE_FORMAT_YMD),
        todate: moment(endDate, DATE_FORMAT_SLASH_MDY).format(DATE_FORMAT_YMD),
        isAll
      });
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchServiceIndicatorsReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchServiceIndicatorsReq`,
  async ({ dealers, monthEndDate, isAll }, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getServiceIndicators({
        dealers,
        month_end_date: moment(monthEndDate, DATE_FORMAT_SLASH_MDY).format(DATE_FORMAT_YMD),
        isAll
      });
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchWindowStickerLeadCountsReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchWindowStickerLeadCountsReq`,
  async ({ dealers, fromDate, toDate, isAll }, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getLeadCounts({
        dealers,
        fromdate: moment(fromDate, DATE_FORMAT_SLASH_MDY).format(DATE_FORMAT_YMD),
        todate: moment(toDate, DATE_FORMAT_SLASH_MDY).format(DATE_FORMAT_YMD),
        isAll
      });
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchInvCapturedCountsReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchInvCapturedCountsReq`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getInvCapturedCounts(payload);
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchInvCountsSingleDealerReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchInvCountsSingleDealerReq`,
  async (obj, { rejectWithValue }) => {
    try {
      const response = await InventoryService.getChartData(obj);
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchInvChartCountMultiDealerReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchInvChartCountMultiDealerReq`,
  async (obj, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getInvChartCount(obj);
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchAvgLifeSpanReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchAvgLifeSpanReq`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getAvgLifeSpan();
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchMainStatsReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetchMainStatsReq`,
  async (obj, { rejectWithValue }) => {
    try {
      const response = await StatsService.getMainStats(obj);
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetcAvgTimeSpentReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetcAvgTimeSpentReq`,
  async (obj, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getAvgTimeSpent(obj);
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetcActiveStatsCountReq = createAsyncThunk(
  `${REQ_DASHBOARD}fetcActiveStatsCountReq`,
  async (obj, { rejectWithValue }) => {
    try {
      const response = await StatsService.getActiveStats();
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

// const setValueOnSelectAll = (state, value) => {
//   const {
//     widget_VehiclesPublished,
//     widget_FlaggedImages,
//     widget_VehicleCounts,
//     widget_ServiceIndicators,
//     widget_WindowStickerStats,
//     widget_TtmChart,
//     widget_ChartVehiclesPublished,
//     widget_InvCapturedCount,
//     widget_CountPA,
//     widget_InvChartsDetails
//   } = current(state);
//   // const value = action.payload;

//   state.isAllDealer = value;
//   state.widget_VehiclesPublished = {
//     ...widget_VehiclesPublished,
//     isAllDealers_VehiclePubToggle: value
//   };
//   state.widget_FlaggedImages = {
//     ...widget_FlaggedImages,
//     isAllDealers_FlaggedImagesToggle: value
//   };
//   state.widget_VehicleCounts = {
//     ...widget_VehicleCounts,
//     isAllDealers_VehicleCountsToggle: value
//   };
//   state.widget_ServiceIndicators = {
//     ...widget_ServiceIndicators,
//     isAllDealers_ServiceIndicatorsToggle: value
//   };
//   state.widget_WindowStickerStats = {
//     ...widget_WindowStickerStats,
//     isAllDealers_WindowStickerStatsToggle: value
//   };
//   state.widget_TtmChart = {
//     ...widget_TtmChart,
//     isAllDealers_TtmChartToggle: value
//   };
//   state.widget_ChartVehiclesPublished = {
//     ...widget_ChartVehiclesPublished,
//     isAllDealers_VehiclePubChartToggle: value
//   };
//   state.widget_InvCapturedCount = {
//     ...widget_InvCapturedCount,
//     isAllDealers_InvCapturedCountToggle: value
//   };
//   state.widget_CountPA = {
//     ...widget_CountPA,
//     isAllDealers_CountPAToggle: value
//   };
//   state.widget_InvChartsDetails = {
//     ...widget_InvChartsDetails,
//     isAllDealers_InvChartsDetailsToggle: value
//   };
// };

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    alldealer: false,
    dashboardDealerChange: false,
    dealershipChange: false,
    // ------ //
    paChartData: [],
    dealerChartData: [],
    countDataLoaded: false,
    paChartDataLoaded: false,

    startDate: moment().toDate(),
    selectedDays: getWeekDays(getWeekRange(moment().toDate()).from),
    dataFilter: [],
    dataFilterDialouge: false,
    allDealersId: [],
    allDealersDetails: [],
    isAllDealer: false,
    isAllDlr: false,
    isDisAllDlr: false,
    presavedSelections: [],
    preSavedId: null,

    widget_TotalActiveStatsCount: {
      video_active_count: 0,
      remove_bg_active_count: 0,
      active_dealers_count: 0
    },
    // widget_AnalyticsVdp: {
    //   fromDate: moment().subtract(29, 'days').startOf('day').toDate(),
    //   toDate: moment().endOf('day').toDate(),
    //   maxDate: moment().endOf('day').toDate(),
    //   isError: false,
    //   isLoaded: false,
    //   vdpData: {
    //     totalActive: 0,
    //     imagesActive: 0,
    //     interiorActive: 0,
    //     exteriorActive: 0,
    //     videoActive: 0,
    //   }
    // },
    widget_TotalActiveChart: {
      avgLife_data: [],
      avgLife_isLoaded: true,
      avgLife_isError: false,
      dataLoaded: false,
      data: [],
      selectedYear: ''
    },
    widget_VehiclesPublished: {
      isAllDealers_VehiclePubToggle: false,
      monthViewData: [],
      isLoaded: true,
      columnDefs: [],
      monthStartDate: moment().toDate(),
      monthCountData: [
        {
          newData: [],
          usedData: [],
          videoData: []
        }
      ]
    },
    widget_FlaggedImages: {
      isAllDealers_FlaggedImagesToggle: false,
      flagYear: new Date().getFullYear(),
      flagChartData: [
        { month: 'Jan' },
        { month: 'Feb' },
        { month: 'Mar' },
        { month: 'Apr' },
        { month: 'May' },
        { month: 'Jun' },
        { month: 'Jul' },
        { month: 'Aug' },
        { month: 'Sep' },
        { month: 'Oct' },
        { month: 'Nov' },
        { month: 'Dec' }
      ],
      flagreportData: {
        from: new Date(),
        to: new Date()
      }
    },
    widget_VehicleCounts: {
      startDate: moment().startOf('month').toDate(),
      endDate: moment().endOf('month').toDate(),
      maxDate: moment().endOf('month').toDate(),
      dataLoaded: false,
      responseStatus: null,
      apSuccess: 0,
      apError: 0,
      apTotal: 0,
      bgSuccess: 0,
      bgError: 0,
      bgTotal: 0,
      success: 0,
      error: 0,
      total: 0,
      isAllDealers_VehicleCountsToggle: false
    },
    widget_ServiceIndicators: {
      data: [],
      errors: [],
      isAllDealers_ServiceIndicatorsToggle: false,
      isServiceIndicatorsLoading: false,
      monthEndDate: moment().toDate()
    },
    widget_WindowStickerStats: {
      fromDate: moment().startOf('month').toDate(),
      toDate: moment().endOf('month').toDate(),
      maxDate: moment().endOf('month').toDate(),
      data: null,
      isLoaded: false,
      isError: false,
      isAllDealers_WindowStickerStatsToggle: false
    },
    widget_TtmChart: {
      ttmYear: 'default',
      ttmChartData: [],
      ttmChartDataLoaded: false,
      isAllDealers_TtmChartToggle: false,
      isXlsDownloading: false
    },
    widget_TurnTrendChart: {
      turnTrendYear: 'default',
      turnTrendChartData: [],
      turnTrendChartDataLoaded: false,
      isAllDealers_TurnTrendChartToggle: false,
      isXlsDownloading: false
    },
    widget_ChartVehiclesPublished: {
      isAllDealers_VehiclePubChartToggle: false,
      publishedChartData: [],
      publishedChartDataLoaded: false,
      publishedLast3Months: 0,
      publishedLast12Months: 0,
      publishedLast3MonthsStartDate: '',
      publishedLast3MonthsEndDate: '',
      publishedLast12MonthsStartDate: '',
      publishedLast12MonthsEndDate: '',
      publishedChartAveragesLoaded: false,
      selectedYear: '',
      selectedYearDealer: [],
      isExcelDownloading: false
    },
    widget_InvCapturedCount: {
      isAllDealers_InvCapturedCountToggle: false,
      dataLoaded: false,
      isXlsLoading: false,
      units: { thisYear: null, lastYear: null, ytdData: 0 },
      flagreportDataExcel: {
        from: new Date(),
        to: new Date()
      }
    },
    widget_CountPA: {
      isAllDealers_CountPAToggle: false,
      countsData: null,
      countDataLoaded: false,
      summaryStartDate: moment().startOf('month').toDate(),
      summaryEndDate: moment().endOf('month').toDate()
    },
    widget_InvChartsDetails: {
      isAllDealers_InvChartsDetailsToggle: false,
      startDate: moment().toDate(),
      endDate: moment().toDate(),
      invChartData: null,
      dataLoaded: false,
      dealerProfileData: {}
    },
    selectedPhotographer: null,
    teamModifiedState: [{ value: '', label: '' }]
  },
  reducers: {
    handleWidget_TotalActiveStatsCount: {
      reducer(state, action) {
        const { video_active_count, remove_bg_active_count, active_dealers_count } = action.payload;
        state.widget_TotalActiveStatsCount = {
          video_active_count,
          remove_bg_active_count,
          active_dealers_count
        };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },
    // handleWidget_AnalyticsVdp: {
    //   reducer(state, action) {
    //     const { fromDate, toDate, maxDate, isError, isLoaded, vdpData } = action.payload;
    //     state.widget_AnalyticsVdp = {
    //       fromDate,
    //       toDate,
    //       maxDate,
    //       isError,
    //       isLoaded,
    //       vdpData
    //     };
    //     return state;
    //   },
    //   prepare(payload) {
    //     return { payload };
    //   }
    // },
    handleWidget_TotalActiveChart: {
      reducer(state, action) {
        const { avgLife_data, avgLife_isLoaded, avgLife_isError, dataLoaded, data, selectedYear } =
          action.payload;
        state.widget_TotalActiveChart = {
          avgLife_data,
          avgLife_isLoaded,
          avgLife_isError,
          dataLoaded,
          data,
          selectedYear
        };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },
    handleWidget_VehiclesPublished: {
      reducer(state, action) {
        const {
          isAllDealers_VehiclePubToggle,
          monthViewData,
          isLoaded,
          columnDefs,
          monthStartDate,
          monthCountData
        } = action.payload;

        state.widget_VehiclesPublished = {
          isAllDealers_VehiclePubToggle,
          monthViewData,
          isLoaded,
          columnDefs,
          monthStartDate,
          monthCountData
        };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },
    handleWidget_FlaggedImages: {
      reducer(state, action) {
        const { isAllDealers_FlaggedImagesToggle, flagYear, flagChartData, flagreportData } = action.payload;
        state.widget_FlaggedImages = {
          isAllDealers_FlaggedImagesToggle,
          flagYear,
          flagChartData,
          flagreportData
        };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },
    handleWidget_VehicleCounts: {
      reducer(state, action) {
        const {
          startDate,
          endDate,
          dataLoaded,
          responseStatus,
          apSuccess,
          apError,
          apTotal,
          bgSuccess,
          bgError,
          bgTotal,
          success,
          error,
          total,
          maxDate,
          isAllDealers_VehicleCountsToggle
        } = action.payload;
        state.widget_VehicleCounts = {
          startDate,
          endDate,
          dataLoaded,
          responseStatus,
          apSuccess,
          apError,
          apTotal,
          bgSuccess,
          bgError,
          bgTotal,
          success,
          error,
          total,
          maxDate,
          isAllDealers_VehicleCountsToggle
        };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },
    handleWidget_ServiceIndicators: {
      reducer(state, action) {
        const {
          isServiceIndicatorsLoading,
          data,
          errors,
          isAllDealers_ServiceIndicatorsToggle,
          monthEndDate
        } = action.payload;
        state.widget_ServiceIndicators = {
          isServiceIndicatorsLoading,
          data,
          errors,
          isAllDealers_ServiceIndicatorsToggle,
          monthEndDate
        };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },
    handleWidget_WindowStickerStats: {
      reducer(state, action) {
        const { fromDate, toDate, maxDate, data, isLoaded, isError, isAllDealers_WindowStickerStatsToggle } =
          action.payload;
        state.widget_WindowStickerStats = {
          fromDate,
          toDate,
          maxDate,
          data,
          isLoaded,
          isError,
          isAllDealers_WindowStickerStatsToggle
        };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },
    handleWidget_TtmChart: {
      reducer(state, action) {
        const { ttmYear, ttmChartData, ttmChartDataLoaded, isAllDealers_TtmChartToggle, isXlsDownloading } =
          action.payload;
        state.widget_TtmChart = {
          ttmYear,
          ttmChartData,
          ttmChartDataLoaded,
          isAllDealers_TtmChartToggle,
          isXlsDownloading
        };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },
    handleWidget_TurnTrendChart: {
      reducer(state, action) {
        const {
          turnTrendYear,
          turnTrendChartData,
          turnTrendChartDataLoaded,
          isAllDealers_TurnTrendChartToggle,
          isXlsDownloading
        } = action.payload;
        state.widget_TurnTrendChart = {
          turnTrendYear,
          turnTrendChartData,
          turnTrendChartDataLoaded,
          isAllDealers_TurnTrendChartToggle,
          isXlsDownloading
        };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },

    handleWidget_ChartVehiclesPublished: {
      reducer(state, action) {
        const {
          isAllDealers_VehiclePubChartToggle,
          publishedChartData,
          publishedChartDataLoaded,
          publishedLast3Months,
          publishedLast12Months,
          publishedChartAveragesLoaded,
          selectedYear,
          selectedYearDealer,
          isExcelDownloading
        } = action.payload;
        state.widget_ChartVehiclesPublished = {
          isAllDealers_VehiclePubChartToggle,
          publishedChartData,
          publishedChartDataLoaded,
          publishedLast3Months,
          publishedLast12Months,
          publishedChartAveragesLoaded,
          selectedYear,
          selectedYearDealer,
          isExcelDownloading
        };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },
    handleWidget_InvCapturedCount: {
      reducer(state, action) {
        const { isAllDealers_InvCapturedCountToggle, dataLoaded, isXlsLoading, units, flagreportDataExcel } =
          action.payload;
        state.widget_InvCapturedCount = {
          isAllDealers_InvCapturedCountToggle,
          dataLoaded,
          isXlsLoading,
          units,
          flagreportDataExcel
        };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },
    handleWidget_CountPA: {
      reducer(state, action) {
        const { isAllDealers_CountPAToggle, countsData, countDataLoaded, summaryStartDate, summaryEndDate } =
          action.payload;
        state.widget_CountPA = {
          isAllDealers_CountPAToggle,
          countsData,
          countDataLoaded,
          summaryStartDate,
          summaryEndDate
        };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },
    handleWidget_InvChartsDetails: {
      reducer(state, action) {
        const {
          isAllDealers_InvChartsDetailsToggle,
          startDate,
          endDate,
          invChartData,
          dataLoaded,
          dealerProfileData
        } = action.payload;
        state.widget_InvChartsDetails = {
          isAllDealers_InvChartsDetailsToggle,
          startDate,
          endDate,
          invChartData,
          dataLoaded,
          dealerProfileData
        };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },
    //
    handleAllDealerChange: {
      reducer(state, action) {
        state.alldealer = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleDashboardDealerChange: {
      reducer(state, action) {
        state.dashboardDealerChange = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleDealershipChange: {
      reducer(state, action) {
        state.dealershipChange = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    // ------------ //
    handlePaChartData: {
      reducer(state, action) {
        state.paChartData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleDealerChartData: {
      reducer(state, action) {
        state.dealerChartData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handlePaChartDataLoaded: {
      reducer(state, action) {
        state.paChartDataLoaded = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleStartDate: {
      reducer(state, action) {
        state.startDate = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleSelectedDays: {
      reducer(state, action) {
        const day = getWeekDays(getWeekRange(action.payload).from);
        state.selectedDays = day;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleDataFilter: {
      reducer(state, action) {
        const value = action.payload;

        const { dataFilter } = current(state);
        if (!arraysAreEqual(dataFilter, value) || !retriveDataFilterStorage()) {
          localStorage.setItem(STORAGE_DATAFILTER, value);
          sessionStorage.setItem(STORAGE_DATAFILTER, value);
          state.dataFilter = value;
        }
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleDialougeDataFilter: {
      reducer(state, action) {
        state.dataFilterDialouge = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleAllDealersId: {
      reducer(state, action) {
        state.allDealersId = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleAllDealersDetails: {
      reducer(state, action) {
        state.allDealersDetails = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsAllDealer: {
      reducer(state, action) {
        //  const value = action.payload;
        // setValueOnSelectAll(state, value);
        const {
          widget_VehiclesPublished,
          widget_FlaggedImages,
          widget_VehicleCounts,
          widget_ServiceIndicators,
          widget_WindowStickerStats,
          widget_TtmChart,
          widget_TurnTrendChart,
          widget_ChartVehiclesPublished,
          widget_InvCapturedCount,
          widget_CountPA,
          widget_InvChartsDetails
        } = current(state);
        const value = action.payload;

        state.isAllDealer = value;
        state.widget_VehiclesPublished = {
          ...widget_VehiclesPublished,
          isAllDealers_VehiclePubToggle: value
        };
        state.widget_FlaggedImages = {
          ...widget_FlaggedImages,
          isAllDealers_FlaggedImagesToggle: value
        };
        state.widget_VehicleCounts = {
          ...widget_VehicleCounts,
          isAllDealers_VehicleCountsToggle: value
        };
        state.widget_ServiceIndicators = {
          ...widget_ServiceIndicators,
          isAllDealers_ServiceIndicatorsToggle: value
        };
        state.widget_WindowStickerStats = {
          ...widget_WindowStickerStats,
          isAllDealers_WindowStickerStatsToggle: value
        };
        state.widget_TtmChart = {
          ...widget_TtmChart,
          isAllDealers_TtmChartToggle: value
        };
        state.widget_TurnTrendChart = {
          ...widget_TurnTrendChart,
          isAllDealers_TurnTrendChartToggle: value
        };
        state.widget_ChartVehiclesPublished = {
          ...widget_ChartVehiclesPublished,
          isAllDealers_VehiclePubChartToggle: value
        };
        state.widget_InvCapturedCount = {
          ...widget_InvCapturedCount,
          isAllDealers_InvCapturedCountToggle: value
        };
        state.widget_CountPA = {
          ...widget_CountPA,
          isAllDealers_CountPAToggle: value
        };
        state.widget_InvChartsDetails = {
          ...widget_InvChartsDetails,
          isAllDealers_InvChartsDetailsToggle: value
        };
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsAllDlr: {
      reducer(state, action) {
        state.isAllDlr = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsDisAllDlr: {
      reducer(state, action) {
        state.isDisAllDlr = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handlePresavedSelections: {
      reducer(state, action) {
        state.presavedSelections = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handlePreSavedId: {
      reducer(state, action) {
        state.preSavedId = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setPhotographer: {
      reducer(state, action) {
        state.selectedPhotographer = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setmModifiedTeam: {
      reducer(state, action) {
        const transformedData = action.payload.map(user => ({
          value: user.user_id,
          label: user.user_displayname
        }));
        state.teamModifiedState = transformedData;
      },
      prepare(payload) {
        return { payload };
      }
    }
  },
  extraReducers: builder => {
    builder
      //#region fetchDashboardCount
      .addCase(fetchDashboardCountPAReq.pending, state => {
        const { widget_CountPA } = current(state);
        state.widget_CountPA = { ...widget_CountPA, countsData: null, countDataLoaded: false };
      })
      .addCase(fetchDashboardCountPAReq.fulfilled, (state, action) => {
        const data = action.payload;
        const { widget_CountPA } = current(state);
        state.widget_CountPA = { ...widget_CountPA, countsData: data, countDataLoaded: true };
      })
      .addCase(fetchDashboardCountPAReq.rejected, state => {
        const { widget_CountPA } = current(state);
        state.widget_CountPA = { ...widget_CountPA, countsData: null, countDataLoaded: true };
      })
      // #endregion

      //#region fetchMonthCountReq
      .addCase(fetchMonthCountReq.pending, state => {
        const { widget_VehiclesPublished } = current(state);
        state.widget_VehiclesPublished = { ...widget_VehiclesPublished, columnDefs: [], isLoaded: true };
      })
      .addCase(fetchMonthCountReq.fulfilled, (state, action) => {
        const data = action.payload;
        const { widget_VehiclesPublished } = current(state);
        state.widget_VehiclesPublished = {
          ...widget_VehiclesPublished,
          isLoaded: false,
          monthCountData: data,
          monthViewData: [
            {
              newData: data?.newRow || [],
              usedData: data?.usedRow || [],
              videoData: data?.videoRow || []
            }
          ]
        };
      })
      .addCase(fetchMonthCountReq.rejected, state => {
        const { widget_VehiclesPublished } = current(state);
        state.widget_VehiclesPublished = { ...widget_VehiclesPublished, isLoaded: false };
      })
      // #endregion

      //#region fetchPublishedChartReq
      .addCase(fetchPublishedChartReq.pending, state => {
        const { widget_ChartVehiclesPublished } = current(state);
        state.widget_ChartVehiclesPublished = {
          ...widget_ChartVehiclesPublished,
          publishedChartData: [],
          publishedChartDataLoaded: false
        };
      })
      .addCase(fetchPublishedChartReq.fulfilled, (state, action) => {
        const data = action.payload;
        const { widget_ChartVehiclesPublished } = current(state);
        state.widget_ChartVehiclesPublished = {
          ...widget_ChartVehiclesPublished,
          publishedChartData: data?.length > 0 ? data : [],
          publishedChartDataLoaded: true
        };
      })
      .addCase(fetchPublishedChartReq.rejected, state => {
        const { widget_ChartVehiclesPublished } = current(state);
        state.widget_ChartVehiclesPublished = {
          ...widget_ChartVehiclesPublished,
          publishedChartData: [],
          publishedChartDataLoaded: true
        };
      })
      // #endregion

      //#region fetchPublishedChartAveragesReq
      .addCase(fetchPublishedChartAveragesReq.pending, state => {
        const { widget_ChartVehiclesPublished } = current(state);
        state.widget_ChartVehiclesPublished = {
          ...widget_ChartVehiclesPublished,
          publishedLast3Months: 0,
          publishedLast12Months: 0,
          publishedLast3MonthsStartDate: '',
          publishedLast3MonthsEndDate: '',
          publishedLast12MonthsStartDate: '',
          publishedLast12MonthsEndDate: '',
          publishedChartAveragesLoaded: false
        };
      })
      .addCase(fetchPublishedChartAveragesReq.fulfilled, (state, action) => {
        const data = action.payload;
        const { widget_ChartVehiclesPublished } = current(state);
        const apiData = data?.length > 0 ? data[0] : null;
        state.widget_ChartVehiclesPublished = {
          ...widget_ChartVehiclesPublished,
          publishedLast3Months: apiData ? apiData?.avgLast3Months : 0,
          publishedLast12Months: apiData ? apiData?.avgLast12Months : 0,
          publishedLast3MonthsStartDate: apiData ? apiData?.last3MonthsStartDate : '',
          publishedLast3MonthsEndDate: apiData ? apiData?.last3MonthsEndDate : '',
          publishedLast12MonthsStartDate: apiData ? apiData?.last12MonthsStartDate : '',
          publishedLast12MonthsEndDate: apiData ? apiData?.last12MonthsEndDate : '',
          publishedChartAveragesLoaded: true
        };
      })
      .addCase(fetchPublishedChartAveragesReq.rejected, state => {
        const { widget_ChartVehiclesPublished } = current(state);
        state.widget_ChartVehiclesPublished = {
          ...widget_ChartVehiclesPublished,
          publishedLast3Months: 0,
          publishedLast12Months: 0,
          publishedLast3MonthsStartDate: '',
          publishedLast3MonthsEndDate: '',
          publishedLast12MonthsStartDate: '',
          publishedLast12MonthsEndDate: '',
          publishedChartAveragesLoaded: true
        };
      })
      // #endregion

      //#region fetchAvgTtmChartReq
      .addCase(fetchAvgTtmChartReq.pending, state => {
        const { widget_TtmChart } = current(state);
        state.widget_TtmChart = {
          ...widget_TtmChart,
          ttmChartData: [],
          ttmChartDataLoaded: false
        };
      })
      .addCase(fetchAvgTtmChartReq.fulfilled, (state, action) => {
        const data = action.payload;
        const { widget_TtmChart } = current(state);
        state.widget_TtmChart = {
          ...widget_TtmChart,
          ttmChartData: data,
          ttmChartDataLoaded: true
        };
      })
      .addCase(fetchAvgTtmChartReq.rejected, state => {
        const { widget_TtmChart } = current(state);
        state.widget_TtmChart = {
          ...widget_TtmChart,
          ttmChartData: [],
          ttmChartDataLoaded: false,
          isAllDealers_TtmChartToggle: false
        };
      })
      // #endregion

      //#region getTurnTrendChartData
      .addCase(fetchTurnTrendChartData.pending, state => {
        const { widget_TurnTrendChart } = current(state);
        state.widget_TurnTrendChart = {
          ...widget_TurnTrendChart,
          turnTrendChartData: [],
          turnTrendChartDataLoaded: false
        };
      })
      .addCase(fetchTurnTrendChartData.fulfilled, (state, action) => {
        const data = action.payload;
        const { widget_TurnTrendChart } = current(state);
        state.widget_TurnTrendChart = {
          ...widget_TurnTrendChart,
          turnTrendChartData: data,
          turnTrendChartDataLoaded: true
        };
      })
      .addCase(fetchTurnTrendChartData.rejected, state => {
        const { widget_TurnTrendChart } = current(state);
        state.widget_TurnTrendChart = {
          ...widget_TurnTrendChart,
          turnTrendChartData: [],
          turnTrendChartDataLoaded: false,
          isAllDealers_TurnTrendChartToggle: false
        };
      })
      // #endregion

      //#region fetchFlagChartReq
      .addCase(fetchFlagChartReq.pending, state => {
        const { widget_FlaggedImages } = current(state);
        state.widget_FlaggedImages = { ...widget_FlaggedImages };
      })
      .addCase(fetchFlagChartReq.fulfilled, (state, action) => {
        const data = action.payload;
        const { widget_FlaggedImages } = current(state);
        state.widget_FlaggedImages = { ...widget_FlaggedImages, flagChartData: data };
      })
      .addCase(fetchFlagChartReq.rejected, state => {
        const { widget_FlaggedImages } = current(state);
        state.widget_FlaggedImages = { ...widget_FlaggedImages };
      })
      // #endregion

      //#region fetchPaProfileReq
      .addCase(fetchPaProfileReq.pending, state => {
        state.paChartData = [];
        state.paChartDataLoaded = false;
      })
      .addCase(fetchPaProfileReq.fulfilled, (state, action) => {
        const data = action.payload;
        state.paChartData = data;
        state.paChartDataLoaded = true;
      })
      .addCase(fetchPaProfileReq.rejected, state => {
        state.paChartData = [];
        state.paChartDataLoaded = false;
      })
      // #endregion

      //#region fetchPresavedSelectionsReq
      .addCase(fetchPresavedSelectionsReq.pending, state => {
        state.presavedSelections = [];
      })
      .addCase(fetchPresavedSelectionsReq.fulfilled, (state, action) => {
        const data = action.payload;
        if (data?.length > 0) {
          state.presavedSelections = data;
        } else {
          state.presavedSelections = [];
        }
      })
      .addCase(fetchPresavedSelectionsReq.rejected, state => {
        state.presavedSelections = [];
      })
      // #endregion

      //#region fetchDealerList4SelectReq
      .addCase(fetchDealerList4SelectReq.fulfilled, (state, action) => {
        const { dataFilter } = current(state);
        const data = action.payload;

        if (data?.length > 0) {
          const { dealers } = data[0];
          // const allDealers_Id = dealers?.map(option => option?.user_id);
          const allDealers_Id = dealers
            ?.filter(f => f.dealer_is_test_dealership === 0)
            ?.map(option => option?.user_id);
          state.allDealersDetails = dealers;

          state.allDealersId = allDealers_Id;

          // all dealerships toggle handled
          const newDataFilter = dataFilter.filter(num => allDealers_Id.includes(num)) || [];
          if (arraysAreEqual(allDealers_Id, newDataFilter)) {
            state.isAllDealer = true;
            // setValueOnSelectAll(state, true);
          } else {
            state.isAllDealer = false;
            // setValueOnSelectAll(state, false);
          }
        } else {
          state.allDealersId = [];
          state.allDealersDetails = [];
        }
      })
      .addCase(fetchDealerList4SelectReq.rejected, state => {
        state.allDealersId = [];
        state.allDealersDetails = [];
        state.isAllDealer = false;
      })
      // #endregion

      //#region fetchVehicleCutoutsReq
      .addCase(fetchVehicleCutoutsReq.pending, state => {
        const { widget_VehicleCounts } = current(state);
        state.widget_VehicleCounts = {
          ...widget_VehicleCounts,
          dataLoaded: false,
          responseStatus: null,
          apSuccess: 0,
          apError: 0,
          apTotal: 0,
          bgSuccess: 0,
          bgError: 0,
          bgTotal: 0,
          success: 0,
          error: 0,
          total: 0
        };
      })
      .addCase(fetchVehicleCutoutsReq.fulfilled, (state, action) => {
        const data = action.payload;
        const { widget_VehicleCounts } = current(state);
        state.widget_VehicleCounts = {
          ...widget_VehicleCounts,
          dataLoaded: true,
          responseStatus: true,
          apSuccess: data?.[0]?.apSuccess,
          apError: data?.[0]?.apError,
          apTotal: data?.[0]?.apTotal,
          bgSuccess: data?.[0]?.bgSuccess,
          bgError: data?.[0]?.bgError,
          bgTotal: data?.[0]?.bgTotal,
          success: data?.[0]?.success,
          error: data?.[0]?.error,
          total: data?.[0]?.total
        };
      })
      .addCase(fetchVehicleCutoutsReq.rejected, state => {
        const { widget_VehicleCounts } = current(state);
        state.widget_VehicleCounts = {
          ...widget_VehicleCounts,
          dataLoaded: false,
          responseStatus: null,
          apSuccess: 0,
          apError: 0,
          apTotal: 0,
          bgSuccess: 0,
          bgError: 0,
          bgTotal: 0,
          success: 0,
          error: 0,
          total: 0,
          isAllDealers_VehicleCountsToggle: false
        };
      })
      // #endregion

      //#region fetchServiceIndicatorsReq
      .addCase(fetchServiceIndicatorsReq.pending, state => {
        const { widget_ServiceIndicators } = current(state);
        state.widget_ServiceIndicators = {
          ...widget_ServiceIndicators,
          data: [],
          isServiceIndicatorsLoading: false,
          errors: []
        };
      })
      .addCase(fetchServiceIndicatorsReq.fulfilled, (state, action) => {
        const data = action.payload;
        const { widget_ServiceIndicators } = current(state);
        state.widget_ServiceIndicators = {
          ...widget_ServiceIndicators,
          data: data?.length > 0 ? data : [],
          errors: data?.length < 1 ? [NODATA] : [],
          isServiceIndicatorsLoading: true
        };
      })
      .addCase(fetchServiceIndicatorsReq.rejected, state => {
        const { widget_ServiceIndicators } = current(state);
        state.widget_ServiceIndicators = {
          ...widget_ServiceIndicators,
          data: [],
          errors: [NODATA],
          isAllDealers_ServiceIndicatorsToggle: false,
          isServiceIndicatorsLoading: true
        };
      })
      // #endregion

      //#region fetchWindowStickerLeadCountsReq
      .addCase(fetchWindowStickerLeadCountsReq.pending, state => {
        const { widget_WindowStickerStats } = current(state);
        state.widget_WindowStickerStats = {
          ...widget_WindowStickerStats,
          data: null,
          isLoaded: false,
          isError: false
        };
      })
      .addCase(fetchWindowStickerLeadCountsReq.fulfilled, (state, action) => {
        const data = action.payload;
        const { widget_WindowStickerStats } = current(state);
        state.widget_WindowStickerStats = {
          ...widget_WindowStickerStats,
          data: data?.length > 0 ? data[0] : null,
          isError: false,
          isLoaded: true
        };
      })
      .addCase(fetchWindowStickerLeadCountsReq.rejected, state => {
        const { widget_WindowStickerStats } = current(state);
        state.widget_WindowStickerStats = {
          ...widget_WindowStickerStats,
          data: null,
          isLoaded: true,
          isError: true,
          isAllDealers_WindowStickerStatsToggle: false
        };
      })
      // #endregion

      //#region fetchInvCapturedCountsReq
      .addCase(fetchInvCapturedCountsReq.pending, state => {
        const { widget_InvCapturedCount } = current(state);
        state.widget_InvCapturedCount = {
          ...widget_InvCapturedCount,
          dataLoaded: false,
          units: { thisYear: null, lastYear: null, ytdData: 0 }
        };
      })
      .addCase(fetchInvCapturedCountsReq.fulfilled, (state, action) => {
        const data = action.payload;
        const { widget_InvCapturedCount } = current(state);
        state.widget_InvCapturedCount = {
          ...widget_InvCapturedCount,
          dataLoaded: true,
          units: data?.length > 0 ? data[0] : null
        };
      })
      .addCase(fetchInvCapturedCountsReq.rejected, state => {
        const { widget_InvCapturedCount } = current(state);
        state.widget_InvCapturedCount = {
          ...widget_InvCapturedCount,
          dataLoaded: true,
          units: { thisYear: null, lastYear: null, ytdData: 0 }
        };
      })
      // #endregion

      //#region fetchInvCountsSingleDealerReq
      .addCase(fetchInvCountsSingleDealerReq.pending, state => {
        const { widget_InvChartsDetails } = current(state);
        state.widget_InvChartsDetails = {
          ...widget_InvChartsDetails,
          invChartData: null,
          dataLoaded: false
        };
      })
      .addCase(fetchInvCountsSingleDealerReq.fulfilled, (state, action) => {
        const data = action.payload;
        const { dataFilter } = action.meta.arg;
        const { widget_InvChartsDetails } = current(state);
        const isPrelimVisible =
          !widget_InvChartsDetails?.isAllDealers_InvChartsDetailsToggle && dataFilter?.length === 1;
        const dataObj = data?.length > 0 ? data[0] : null;
        const arr = [
          {
            id: 'Total',
            title: 'Total Vehicles',
            data: dataObj?.invTotal,
            type: 'dash',
            isPrelimVisible
          },
          { id: 'New', title: 'New Vehicles', data: dataObj?.invNew, type: 'dash' },
          {
            id: 'Used',
            title: 'Pre Owned Vehicles',
            data: dataObj?.invUsed,
            type: 'dash',
            isPrelimVisible
          }
        ];

        state.widget_InvChartsDetails = {
          ...widget_InvChartsDetails,
          invChartData: arr,
          dataLoaded: true
        };
      })
      .addCase(fetchInvCountsSingleDealerReq.rejected, state => {
        const { widget_InvChartsDetails } = current(state);
        state.widget_InvChartsDetails = {
          ...widget_InvChartsDetails,
          invChartData: null,
          dataLoaded: true
        };
      })
      // #endregion

      //#region fetchInvChartCountMultiDealerReq
      .addCase(fetchInvChartCountMultiDealerReq.pending, state => {
        const { widget_InvChartsDetails } = current(state);
        state.widget_InvChartsDetails = {
          ...widget_InvChartsDetails,
          invChartData: null,
          dataLoaded: false
        };
      })
      .addCase(fetchInvChartCountMultiDealerReq.fulfilled, (state, action) => {
        const data = action.payload;
        const { dealers } = action.meta.arg;
        const { widget_InvChartsDetails } = current(state);
        const isPrelimVisible =
          !widget_InvChartsDetails?.isAllDealers_InvChartsDetailsToggle && dealers?.length === 1;
        const dataObj = data?.length > 0 ? data[0] : null;
        // const startDate = dataObj.fromDate;
        // const endDate = dataObj.toDate;
        const arr = [
          {
            id: 'Total',
            title: 'Total Vehicles',
            data: dataObj?.invTotal,
            type: 'dash',
            isPrelimVisible
          },
          { id: 'New', title: 'New Vehicles', data: dataObj?.invNew, type: 'dash' },
          {
            id: 'Used',
            title: 'Pre Owned Vehicles',
            data: dataObj?.invUsed,
            type: 'dash',
            isPrelimVisible
          }
        ];

        state.widget_InvChartsDetails = {
          ...widget_InvChartsDetails,
          // startDate,
          // endDate,
          invChartData: arr,
          dataLoaded: true
        };
      })
      .addCase(fetchInvChartCountMultiDealerReq.rejected, state => {
        const { widget_InvChartsDetails } = current(state);
        state.widget_InvChartsDetails = {
          ...widget_InvChartsDetails,
          invChartData: null,
          dataLoaded: true
        };
      })
      // #endregion

      //#region fetchDmsImportExportReq
      .addCase(fetchDmsImportExportReq.pending, state => {
        const { widget_InvChartsDetails } = current(state);
        state.widget_InvChartsDetails = {
          ...widget_InvChartsDetails,
          dealerProfileData: {}
        };
      })
      .addCase(fetchDmsImportExportReq.fulfilled, (state, action) => {
        const { widget_InvChartsDetails } = current(state);
        state.widget_InvChartsDetails = {
          ...widget_InvChartsDetails,
          dealerProfileData: action.payload
        };
      })
      .addCase(fetchDmsImportExportReq.rejected, state => {
        const { widget_InvChartsDetails } = current(state);
        state.widget_InvChartsDetails = {
          ...widget_InvChartsDetails,
          dealerProfileData: {}
        };
      })
      // #endregion

      //#region fetchMainStatsReq
      .addCase(fetchMainStatsReq.pending, state => {
        const { widget_TotalActiveChart } = current(state);
        state.widget_TotalActiveChart = {
          ...widget_TotalActiveChart,
          data: [],
          dataLoaded: false
        };
      })
      .addCase(fetchMainStatsReq.fulfilled, (state, action) => {
        const { widget_TotalActiveChart } = current(state);
        state.widget_TotalActiveChart = {
          ...widget_TotalActiveChart,
          data: action.payload ?? [],
          dataLoaded: true
        };
      })
      .addCase(fetchMainStatsReq.rejected, state => {
        const { widget_TotalActiveChart } = current(state);
        state.widget_TotalActiveChart = {
          ...widget_TotalActiveChart,
          data: [],
          dataLoaded: true
        };
      })
      // #endregion

      //#region fetchAvgLifeSpanReq
      .addCase(fetchAvgLifeSpanReq.pending, state => {
        const { widget_TotalActiveChart } = current(state);
        state.widget_TotalActiveChart = {
          ...widget_TotalActiveChart,
          avgLife_data: [],
          avgLife_isLoaded: true,
          avgLife_isError: false
        };
      })
      .addCase(fetchAvgLifeSpanReq.fulfilled, (state, action) => {
        const { widget_TotalActiveChart } = current(state);
        state.widget_TotalActiveChart = {
          ...widget_TotalActiveChart,
          avgLife_data: action.payload ?? [],
          avgLife_isLoaded: false,
          avgLife_isError: false
        };
      })
      .addCase(fetchAvgLifeSpanReq.rejected, state => {
        const { widget_TotalActiveChart } = current(state);
        state.widget_TotalActiveChart = {
          ...widget_TotalActiveChart,
          avgLife_data: [],
          avgLife_isLoaded: false,
          avgLife_isError: true
        };
      })
      // #endregion

      //#region fetcAvgTimeSpentReq
      // .addCase(fetcAvgTimeSpentReq.pending, state => {
      //   const { widget_AnalyticsVdp } = current(state);
      //   state.widget_AnalyticsVdp = {
      //     ...widget_AnalyticsVdp,
      //     isError: false,
      //     isLoaded: false
      //   };
      // })
      // .addCase(fetcAvgTimeSpentReq.fulfilled, (state, action) => {
      //   const { widget_AnalyticsVdp } = current(state);
      //   state.widget_AnalyticsVdp = {
      //     ...widget_AnalyticsVdp,
      //     vdpData: action.payload?.[0] ?? {},
      //     isLoaded: true,
      //     isError: false
      //   };
      // })
      // .addCase(fetcAvgTimeSpentReq.rejected, state => {
      //   const { widget_AnalyticsVdp } = current(state);
      //   state.widget_AnalyticsVdp = {
      //     ...widget_AnalyticsVdp,
      //     vdpData: {},
      //     isLoaded: true,
      //     isError: false
      //   };
      // })
      // #endregion

      //#region fetcActiveStatsCountReq
      .addCase(fetcActiveStatsCountReq.pending, state => {
        const { widget_TotalActiveStatsCount } = current(state);
        state.widget_TotalActiveStatsCount = {
          ...widget_TotalActiveStatsCount,
          video_active_count: 0,
          remove_bg_active_count: 0,
          active_dealers_count: 0
        };
      })
      .addCase(fetcActiveStatsCountReq.fulfilled, (state, action) => {
        const data = action.payload;
        const { widget_TotalActiveStatsCount } = current(state);
        state.widget_TotalActiveStatsCount = {
          ...widget_TotalActiveStatsCount,
          video_active_count: data?.length > 0 ? data[0]?.video_active : 0,
          remove_bg_active_count: data?.length > 0 ? data[0]?.remove_bg_active : 0,
          active_dealers_count: data?.length > 0 ? data[0]?.active_dealers : 0
        };
      })
      .addCase(fetcActiveStatsCountReq.rejected, state => {
        const { widget_TotalActiveStatsCount } = current(state);
        state.widget_TotalActiveStatsCount = {
          ...widget_TotalActiveStatsCount,
          video_active_count: 0,
          remove_bg_active_count: 0,
          active_dealers_count: 0
        };
      });
    // #endregion
  }
});

export const {
  handleWidget_TotalActiveStatsCount,
  // handleWidget_AnalyticsVdp,
  handleWidget_TotalActiveChart,
  handleWidget_VehiclesPublished,
  handleWidget_FlaggedImages,
  handleWidget_VehicleCounts,
  handleWidget_ServiceIndicators,
  handleWidget_WindowStickerStats,
  handleWidget_TtmChart,
  handleWidget_TurnTrendChart,
  handleWidget_ChartVehiclesPublished,
  handleWidget_InvCapturedCount,
  handleWidget_CountPA,
  handleWidget_InvChartsDetails,
  //
  handleAllDealerChange,
  handleDashboardDealerChange,
  handleDealershipChange,
  // ------------ //
  handlePaChartData,
  setPhotographer,
  handleDealerChartData,
  handlePaChartDataLoaded,
  handleTtmChartData,
  handleStartDate,
  handleSelectedDays,
  handleDataFilter,
  handleDialougeDataFilter,
  handleAllDealersId,
  handleAllDealersDetails,
  handleIsAllDealer,
  handleIsAllDlr,
  handleIsDisAllDlr,
  handlePresavedSelections,
  handlePreSavedId,
  setmModifiedTeam
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
